import { inject, Injectable } from '@angular/core';
import { BrowserCacheLocation, Configuration, PublicClientApplication } from '@azure/msal-browser';

import { WINDOW } from './window.service';

function getMsalConfig(windowRef: Window): Configuration {
  return {
    auth: {
      clientId: 'b1204b2f-b2b3-4d32-9722-b95afcb6acca',
      authority: 'https://login.microsoftonline.com/dc81d03c-239c-4fd5-a96f-18a58773c86c/',
      redirectUri: `${windowRef.location.origin}/service/`,
      postLogoutRedirectUri: 'https://www.qiagen.com'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    }
  };
}

/**
 * Thin wrapper around the MSAL (Microsoft Authentication Library) browser
 * client, ensuring a single PublicClientApplication instance is shared
 * across the application.
 *
 * Exists because MSAL requires explicit async initialization before any
 * token operations, and creating multiple instances causes cache conflicts.
 * The initialize() guard ensures the MSAL internal state machine is ready
 * before ServiceAccountAuthService attempts silent or interactive token
 * acquisition.
 *
 * Only used in the service-account ("/service") login flow; CAS-based
 * human authentication does not involve MSAL.
 */
@Injectable({
  providedIn: 'root'
})
export class MSALService {
  private readonly window = inject(WINDOW);
  private readonly msalInstance: PublicClientApplication;
  private initialized = false;

  constructor() {
    this.msalInstance = new PublicClientApplication(getMsalConfig(this.window));
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    try {
      await this.msalInstance.initialize();
      const result = await this.msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      if (result?.account) {
        this.msalInstance.setActiveAccount(result.account);
      }
    } catch (err) {
      // Don't let MSAL redirect-handling failures kill the whole bootstrap.
      // Common causes: stale interaction state in localStorage from a prior
      // deploy, network blip to login.microsoftonline.com, AAD config drift.
      // Logging + clearing the stuck interaction flag lets the user fall
      // through to redirectToLogin() which restarts a clean auth flow
      // instead of leaving the app dead.
      console.error('[MSAL] initialize/handleRedirectPromise failed', err);
      this.clearStuckInteractionState();
    } finally {
      this.initialized = true;
    }
  }

  getInstance(): PublicClientApplication {
    return this.msalInstance;
  }

  /**
   * Manually removes the `msal.<clientId>.interaction.status` cache entry
   * that MSAL sets to `"in_progress"` at the start of every redirect flow.
   *
   * When a redirect aborts mid-flight (page reload, network failure, a
   * second concurrent caller of `loginRedirect`) the flag is left set, and
   * every subsequent `loginRedirect()` rejects immediately with
   * `BrowserAuthError: interaction_in_progress`. Without explicit cleanup
   * the only recovery is for the user to clear site data by hand.
   *
   * Cleared from both storages defensively: the configured cacheLocation
   * is currently LocalStorage but historical builds used SessionStorage,
   * so a leftover key from a prior deploy could live in either.
   */
  clearStuckInteractionState(): void {
    try {
      const clientId = this.msalInstance.getConfiguration().auth.clientId;
      const key = `msal.${clientId}.interaction.status`;
      this.window.localStorage?.removeItem(key);
      this.window.sessionStorage?.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}
